import { useEffect, useState } from 'react';
import {
  CustomModal,
  ModuleView,
  theme,
  Select,
  Input,
  CustomRadioInput,
} from '@jadwalh/ui';
import { Button, Flex } from '@mantine/core';
import { EventsWrapper, Wrapper } from './styles';
import { useIntl } from 'react-intl';
import { BiPlusCircle } from 'react-icons/bi';
import { ReactComponent as StarIcon } from '../../../assets/images/svg/sparkle.svg';
import { ReactComponent as EditIcon } from '../../../assets/images/svg/editPenIcon.svg';
import { Controller, useFormContext } from 'react-hook-form';
import { getEnumsData } from '../../../store/enums.slice';
import { setErrors } from '@jadwalh/utils';
import { ErrorMessage } from '@hookform/error-message';
import { updateTrigger } from '../../../api/workflow.api';
import { useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';

export default function Events({
  data,
  refetch,
}: {
  data: any;
  refetch: () => void;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { formatMessage } = useIntl();
  const { id } = useParams();
  const {
    control,
    watch,
    setValue,
    clearErrors,
    handleSubmit,
    setError,
    formState: { errors },
  } = useFormContext();
  const enumsData = useSelector(getEnumsData);
  const [eventData, setEventData] = useState<any>(data);

  const { mutateAsync, isLoading: isTriggerUpdating } = useMutation({
    mutationFn: (data: any) => {
      return updateTrigger(data, Number(id));
    },
    onSuccess: (response: any) => {
      toast.success(formatMessage({ id: 'newAppointmentAdded' }));
      setIsModalOpen(false);
      window.scrollTo(0, 0);
      setEventData({
        trigger: response.trigger,
        trigger_execute_time: response.trigger_execute_time,
        trigger_time_unit: response.trigger_time_unit,
        trigger_value: response.trigger_value,
        duration_event: response.duration_event,
        id: response.id,
      });
    },
    onError: (error: any) => {
      const errors = error.response.data.errors;
      setErrors(errors, setError);
    },
  });

  const addTrigger = () => {
    const trigger = watch('trigger_value');
    const triggerExecuteTime = watch('trigger_execute_time');
    const timeUnit = watch('trigger_time_unit');

    mutateAsync({
      id: eventData?.id,
      trigger: Number(trigger),
      ...(triggerExecuteTime && {
        trigger_execute_time: Number(triggerExecuteTime),
      }),
      ...(timeUnit && { trigger_time_unit: Number(timeUnit) }),
    });
  };
  const handleOpenModal = () => {
    setIsModalOpen(true);
    setValue('trigger_value', eventData?.trigger_value?.toString());
    setValue(
      'trigger_execute_time',
      eventData?.trigger_execute_time?.toString()
    );
    setValue('trigger_time_unit', eventData?.trigger_time_unit?.toString());
    setValue('duration_event', eventData?.duration_event?.toString());
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleEditEvent = () => {
    handleOpenModal();
  };


  const resetForm = () => {
    setValue('trigger_value', '');
    setValue('trigger_execute_time', '');
    setValue('trigger_time_unit', '');
    setValue('duration_event', '0');
  };

  useEffect(() => {
    if (eventData?.trigger_value?.toString()) {
      setValue('trigger_value', eventData?.trigger_value.toString());
    }
    if (eventData?.trigger_execute_time?.toString()) {
      setValue('trigger_execute_time', eventData?.trigger_execute_time);
    }
    if (eventData?.trigger_time_unit?.toString()) {
      setValue('trigger_time_unit', eventData?.trigger_time_unit.toString());
    }
    if (eventData?.duration_event?.toString()) {
      setValue('duration_event', eventData?.duration_event.toString());
    }
  }, [eventData]);

  const eventTypeWatch = watch('trigger_value');

  return (
    <ModuleView>
      <>
        <Wrapper>
          <Flex direction="column">
            <h2>{formatMessage({ id: 'whenHappen' })}</h2>
            {eventData?.trigger ? (
              <Flex direction="column" gap={'8px'}>
                <ul className="question_container">
                  <li className="container" style={{ padding: '8px 16px' }}>
                    <Flex justify="space-between" align="start">
                      <Flex direction="column" gap={4}>
                        <p>{eventData?.trigger}</p>
                      </Flex>
                      <Flex
                        align="center"
                        gap={16}
                        className="question-actions"
                      >
                        <EditIcon
                          onClick={() => handleEditEvent()}
                          style={{
                            cursor: 'pointer',
                            marginTop: '4px',
                            color: '#25314C',
                          }}
                        />
                      </Flex>
                    </Flex>
                  </li>
                </ul>
                {!eventData?.trigger && (
                  <button
                    type="button"
                    className={`text-link`}
                    onClick={handleOpenModal}
                  >
                    <BiPlusCircle />
                    {formatMessage({ id: 'addTrigger' })}
                  </button>
                )}
              </Flex>
            ) : (
              <Flex
                justify={'center'}
                align={'center'}
                direction={'column'}
                p={'32px'}
                pb={'16px'}
                gap={'16px'}
              >
                <StarIcon />
                <p className="desc">
                  {formatMessage({ id: 'whatHappenDescInWorkflow' })}
                </p>
                <button
                  type="button"
                  className="text-link"
                  onClick={handleOpenModal}
                >
                  <BiPlusCircle />
                  <span>{formatMessage({ id: 'addTrigger' })}</span>
                </button>
              </Flex>
            )}
          </Flex>
        </Wrapper>
        {isModalOpen && (
          <CustomModal
            opened={isModalOpen}
            onClose={handleCloseModal}
            withCloseButton={false}
            size="529px"
            closeOnEscape={false}
            header={formatMessage({ id: 'whenHappen' })}
          >
            <EventsWrapper>
              <Flex direction="column" gap={'24px'}>
                <Controller
                  control={control}
                  name="trigger_value"
                  rules={{
                    required: {
                      value: true,
                      message: formatMessage({ id: 'requiredField' }),
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="trigger_value"
                      searchable={false}
                      label={formatMessage({ id: 'whenHappen' })}
                      placeholder={formatMessage({
                        id: 'choose',
                      })}
                      withinPortal={true}
                      data={enumsData?.enums?.triggers_enum || []}
                      onChange={(value) => {
                        resetForm();
                        onChange(value);
                        clearErrors();
                      }}
                      value={value as string}
                    />
                  )}
                />
                {eventTypeWatch && eventTypeWatch !== '0' && (
                  <Flex direction="column" gap={'4px'}>
                    <p
                      style={{
                        fontSize: '14px',
                        fontWeight: '400',
                        lineHeight: '22px',
                        color: '#303030',
                      }}
                    >
                      {eventTypeWatch === '1' &&
                        formatMessage({ id: 'howMuchDuration' })}
                      {eventTypeWatch === '2' &&
                        formatMessage({ id: 'howMuchDurationAfterEvent' })}
                      {eventTypeWatch === '3' &&
                        formatMessage({ id: 'howMuchAfterReschedule' })}
                      {eventTypeWatch === '4' &&
                        formatMessage({
                          id: 'howMuchDurationAfterEventFinish',
                        })}
                      {eventTypeWatch === '5' &&
                        formatMessage({ id: 'howMuchDurationCancelEvent' })}
                    </p>
                    {eventTypeWatch !== '1' &&
                      eventTypeWatch !== '0' &&
                      eventTypeWatch && (
                        <div
                          className={`${
                            errors.duration_event ? 'hasError' : ''
                          }`}
                        >
                          <Controller
                            control={control}
                            name="duration_event"
                            rules={{
                              required: formatMessage({ id: 'requiredField' }),
                            }}
                            render={({ field: { onChange, value } }) => (
                              <CustomRadioInput
                                name="duration_event"
                                className="custom-radio-input"
                                label={formatMessage({
                                  id: 'afterFutureDuration',
                                })}
                                value="0"
                                onChange={(value) => {
                                  if (value) {
                                    onChange(value);
                                    setValue('duration_event', value);
                                    clearErrors('duration_event');
                                    setValue('trigger_execute_time', '');
                                    setValue('trigger_time_unit', '');
                                    clearErrors('trigger_time_unit');
                                    clearErrors('trigger_execute_time');
                                  }
                                }}
                              />
                            )}
                          />
                        </div>
                      )}
                    {/* {eventTypeWatch === '0' && (
                      <div
                        className={`${errors.duration_event ? 'hasError' : ''}`}
                      >
                        <Controller
                          control={control}
                          name="duration_event"
                          rules={{
                            required: formatMessage({ id: 'requiredField' }),
                          }}
                          render={({ field: { onChange, value } }) => (
                            <CustomRadioInput
                              className="custom-radio-input"
                              name="duration_event"
                              label={formatMessage({ id: 'afterEventBooking' })}
                              value="1"
                              onChange={(radioValue) => {
                                if (radioValue) {
                                  onChange(radioValue);
                                  setValue('duration_event', radioValue, {
                                    shouldValidate: true,
                                  });
                                  clearErrors('duration_event');
                                }
                              }}
                              checked={watch('duration_event') === '1'}
                            />
                          )}
                        />
                      </div>
                    )} */}
                    {eventTypeWatch === '2' && (
                      <div
                        className={`${errors.duration_event ? 'hasError' : ''}`}
                      >
                        <Controller
                          control={control}
                          name="duration_event"
                          rules={{
                            required: formatMessage({ id: 'requiredField' }),
                          }}
                          render={({ field: { onChange, value } }) => (
                            <CustomRadioInput
                              name="duration_event"
                              className="custom-radio-input"
                              label={formatMessage({ id: 'afterEvent' })}
                              value="1"
                              onChange={(value) => {
                                if (value) {
                                  onChange(value);
                                  setValue('duration_event', value, {
                                    shouldValidate: true,
                                  });
                                  clearErrors('duration_event');
                                }
                              }}
                              checked={watch('duration_event') === '1'}
                            />
                          )}
                        />
                      </div>
                    )}{' '}
                    {eventTypeWatch === '3' && (
                      <div
                        className={`${errors.duration_event ? 'hasError' : ''}`}
                      >
                        <Controller
                          control={control}
                          name="duration_event"
                          rules={{
                            required: formatMessage({ id: 'requiredField' }),
                          }}
                          render={({ field: { onChange, value } }) => (
                            <CustomRadioInput
                              className="custom-radio-input"
                              name="duration_event"
                              label={formatMessage({ id: 'afterReschedule' })}
                              value="1"
                              onChange={(value) => {
                                if (value) {
                                  onChange(value);
                                  setValue('duration_event', value, {
                                    shouldValidate: true,
                                  });
                                  clearErrors('duration_event');
                                }
                              }}
                              checked={watch('duration_event') === '1'}
                            />
                          )}
                        />
                      </div>
                    )}
                    {eventTypeWatch === '4' && (
                      <div
                        className={`${errors.duration_event ? 'hasError' : ''}`}
                      >
                        <Controller
                          control={control}
                          name="duration_event"
                          rules={{
                            required: formatMessage({ id: 'requiredField' }),
                          }}
                          render={({ field: { onChange, value } }) => (
                            <CustomRadioInput
                              className="custom-radio-input"
                              name="duration_event"
                              label={formatMessage({ id: 'afterFinishEvent' })}
                              value="1"
                              onChange={(value) => {
                                if (value) {
                                  onChange(value);
                                  setValue('duration_event', value, {
                                    shouldValidate: true,
                                  });
                                  clearErrors('duration_event');
                                }
                              }}
                              checked={watch('duration_event') === '1'}
                            />
                          )}
                        />
                      </div>
                    )}
                    {eventTypeWatch === '5' && (
                      <div
                        className={`${errors.duration_event ? 'hasError' : ''}`}
                      >
                        <Controller
                          control={control}
                          name="duration_event"
                          rules={{
                            required: formatMessage({ id: 'requiredField' }),
                          }}
                          render={({ field: { onChange, value } }) => (
                            <CustomRadioInput
                              name="duration_event"
                              className="custom-radio-input"
                              label={formatMessage({ id: 'afterCancelEvent' })}
                              value="1"
                              onChange={(value) => {
                                if (value) {
                                  onChange(value);
                                  setValue('duration_event', value, {
                                    shouldValidate: true,
                                  });
                                  clearErrors('duration_event');
                                }
                              }}
                              checked={watch('duration_event') === '1'}
                            />
                          )}
                        />
                      </div>
                    )}
                    {((watch('duration_event') === '1' &&
                      eventTypeWatch !== '0') ||
                      eventTypeWatch === '1') && (
                      <Flex direction="column" gap={'0px'}>
                        <Flex
                          className={`${
                            errors.trigger_execute_time ? 'hasError' : ''
                          } number_input_select_wrapper`}
                        >
                          <Controller
                            control={control}
                            name="trigger_execute_time"
                            rules={
                              watch('duration_event') === '1' ||
                              eventTypeWatch === '1'
                                ? {
                                    required: formatMessage({
                                      id: 'requiredField',
                                    }),
                                    min: {
                                      value: 1,
                                      message: formatMessage(
                                        { id: 'numberValidation' },
                                        { min: 1 }
                                      ),
                                    },
                                    max: {
                                      value: 999,
                                      message: formatMessage(
                                        { id: 'maxNumberValidation' },
                                        { max: 999 }
                                      ),
                                    },
                                  }
                                : {}
                            }
                            render={({ field: { onChange, value } }) => (
                              <Input
                                type="number"
                                className="input_number_select"
                                name="trigger_execute_time"
                                onChange={(event) => {
                                  setValue(
                                    'trigger_execute_time',
                                    event.target.value
                                  );
                                  onChange(event.target.value);
                                  clearErrors('trigger_execute_time');
                                }}
                                max={999}
                                min={1}
                                value={value}
                              />
                            )}
                          />
                          <div className="hide_label">
                            <Controller
                              control={control}
                              name="trigger_time_unit"
                              rules={
                                watch('duration_event') === '1' ||
                                eventTypeWatch === '1'
                                  ? {
                                      required: formatMessage({
                                        id: 'requiredField',
                                      }),
                                    }
                                  : {}
                              }
                              render={({ field: { onChange, value } }) => (
                                <Select
                                  name="trigger_time_unit"
                                  searchable={false}
                                  data={
                                    enumsData.enums
                                      ?.event_duration_unit_enums || []
                                  }
                                  onChange={(value) => {
                                    onChange(value);
                                    clearErrors('trigger_time_unit');
                                    setValue('trigger_time_unit', value);
                                  }}
                                  value={value as any}
                                  placeholder={formatMessage({
                                    id: 'choose',
                                  })}
                                />
                              )}
                            />
                          </div>
                        </Flex>
                        {!!errors && !!errors['trigger_time_unit'] && (
                          <div className="ErrorMessageStyle">
                            <ErrorMessage
                              errors={errors}
                              name={'trigger_time_unit'}
                              render={({ message }: any) => <p>{message}</p>}
                            />
                          </div>
                        )}
                        {!!errors &&
                          !!errors['trigger_execute_time'] &&
                          !errors['trigger_time_unit'] && (
                            <div className="ErrorMessageStyle">
                              <ErrorMessage
                                errors={errors}
                                name={'trigger_execute_time'}
                                render={({ message }: any) => <p>{message}</p>}
                              />
                            </div>
                          )}
                      </Flex>
                    )}
                  </Flex>
                )}

                {/* {eventTypeWatch === '0' && (
                  <Checkbox
                    name="show_setting_group_event"
                    onChange={(value) => {
                      setValue('show_setting_group_event', value ? 1 : 0);
                    }}
                    // checked={watch('show_setting_group_event') === '1'}
                    defaultChecked={watch('show_setting_group_event') === '1'}
                    className="event-checkbox"
                    label={formatMessage({
                      id: 'eventSettings',
                    })}
                  />
                )} */}
              </Flex>
            </EventsWrapper>
            <Flex
              justify={'start'}
              style={{
                borderTop: `1px solid ${theme.colours.newOutline}`,
                padding: '12px 16px',
              }}
              gap={12}
            >
              <Button
                type="button"
                onClick={() => {
                  handleSubmit(addTrigger)();
                }}
                loading={isTriggerUpdating}
                style={{
                  backgroundColor: theme.colours.blackText,
                  borderRadius: '8px',
                  color: theme.colours.white,
                  minWidth: '80px',
                  height: '32px',
                }}
              >
                {!isTriggerUpdating &&
                  (watch('trigger_value')
                    ? formatMessage({ id: 'save' })
                    : formatMessage({ id: 'add' }))}
              </Button>
              <Button
                onClick={handleCloseModal}
                style={{
                  backgroundColor: theme.colours.white,
                  borderRadius: '8px',
                  color: theme.colours.blackText,
                  border: `1px solid ${theme.colours.newOutline}`,
                  minWidth: '80px',
                  height: '32px',
                }}
              >
                {formatMessage({ id: 'cancel' })}
              </Button>
            </Flex>
          </CustomModal>
        )}
      </>
    </ModuleView>
  );
}
